import { useEffect, useMemo, useState } from 'react';
import type {
  AdminRosterResponse, AdminRosterRow, ClaimRequestsResponse, ClaimRequestRow,
  SetTeamResponse, Team,
} from '@mv/contracts';
import { TEAMS, normaliseFlight } from '@mv/contracts';
import { api } from '../lib/api';
import Flag from './Flag';
import { IconClose, IconUnlink } from './Icon';
import { fmtDay, fmtTime, now, relativeAgo } from '../lib/time';

/**
 * The organiser's roster controls (D59 §7).
 *
 * **This is more capability than an organiser holds anywhere else, and that is
 * deliberate rather than an oversight.** D37 and D50 kept the admin surface to
 * featuring and unfeaturing: reversible, non-destructive, and a wrong one is
 * undone by another organiser. Reassigning an identity is larger. It is here
 * because every failure path a guest cannot resolve alone ends at a human — a
 * name somebody else took, a plus-one not on the list, a row seeded with a name
 * spelled wrong — and without this the alternative for a stranded guest is
 * nothing at all.
 *
 * It is read-first and written rarely. The queue is the to-do list; the roster
 * below it answers two different questions, and it used to answer only one.
 *
 * **"Who has signed in?" is a survey, not a lookup.** The roster began as a
 * search box that rendered nothing until you typed, capped at twelve hits —
 * right for the guest standing in front of you, useless for the question an
 * organiser actually asks in the week before: who has claimed a name so far,
 * and under which address. So the whole list is shown by default, filterable by
 * claim state, with the email on every matched row rather than only as a
 * fallback for a guest who never set a display name.
 *
 * The search box stays, and while a queue entry is being matched it still gates
 * the list — assigning is a targeted act, and a hundred and fifty-nine
 * "give them this name" buttons is a mis-tap waiting to happen.
 */
export default function AdminRoster() {
  const [roster, setRoster] = useState<AdminRosterResponse | null>(null);
  const [requests, setRequests] = useState<ClaimRequestRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [q, setQ] = useState('');
  /** Who we are matching a name to, while the search below picks which name. */
  const [assigning, setAssigning] = useState<ClaimRequestRow | null>(null);
  /**
   * Which slice of the roster is listed. Everyone, by default.
   *
   * **This has now defaulted three ways and the history is the argument.** It
   * began as a lookup that listed nothing until you typed — right for the guest
   * standing in front of you, useless for "who has signed in so far". That
   * became `all`, which answered the survey and buried the controls under a
   * hundred and fifty-nine cards. So a fourth state was added, `none`, and the
   * list waited to be asked for.
   *
   * `none` is gone again, and not because it was wrong: it was solving for a
   * page that had a whole second screen — the guest list — underneath the
   * roster. That screen has been absorbed, the roster *is* the page, and the
   * only things above it now are the queue, the counts and the search. There is
   * nothing left for a long list to bury, so making an organiser ask for it
   * every time is a click that buys nothing.
   */
  const [filter, setFilter] = useState<'all' | 'matched' | 'unmatched'>('all');
  /**
   * The team move an organiser has picked but not yet confirmed (D54).
   *
   * Confirmed rather than immediate, and that is the part worth keeping from
   * the screen this absorbed: the control is a list of eight countries on a
   * phone being passed around a table at a welcome dinner, and a mis-tap that
   * silently reassigns somebody is the failure it was designed against.
   */
  const [pendingTeam, setPendingTeam] = useState<{ row: AdminRosterRow; team: Team } | null>(null);
  const [teamNote, setTeamNote] = useState<string | null>(null);

  useEffect(() => { void reload(); }, []);
  /**
   * The clock the "Claimed" and "Seen" labels count from, moved on once a
   * minute (D74). They say "2 min ago", and this page is left open at a
   * check-in desk; without a tick that label would say the same thing an hour
   * later. It moves the labels, not the data: a reload is still what learns
   * that somebody has been seen since.
   */
  const [at, setAt] = useState(now);
  useEffect(() => {
    const id = setInterval(() => setAt(now()), 60_000);
    return () => clearInterval(id);
  }, []);

  async function reload() {
    setError(null);
    try {
      const [r, c] = await Promise.all([
        api<AdminRosterResponse>('/admin/roster'),
        api<ClaimRequestsResponse>('/admin/claim-requests'),
      ]);
      setRoster(r); setRequests(c.requests);
    } catch {
      setError('Could not load the roster.');
    }
  }

  /**
   * Returns whether it worked, and callers must honour that.
   *
   * It used to swallow the failure and return nothing, so the assign handler's
   * `.then()` ran on failure exactly as on success — clearing the organiser's
   * in-progress match and the name they had typed. "Try again" then meant
   * re-opening the queue entry and re-typing a guest's name from scratch, while
   * standing in front of that guest, which is the one moment this control was
   * built for.
   */
  async function run(key: string, fn: () => Promise<unknown>): Promise<boolean> {
    setBusy(key); setError(null);
    try { await fn(); await reload(); return true; }
    catch { setError('That did not go through. Try again.'); return false; }
    finally { setBusy(null); }
  }

  const assign = (guestId: string, userId: string) =>
    run(guestId, () => api(`/admin/roster/${guestId}/assign`, { method: 'POST', json: { userId } }));
  const unclaim = (guestId: string) =>
    run(guestId, () => api(`/admin/roster/${guestId}/claim`, { method: 'DELETE' }));
  const dismiss = (userId: string) =>
    run(userId, () => api(`/admin/claim-requests/${userId}`, { method: 'DELETE' }));

  /**
   * Moves the **account** onto a team, not the roster row (D54).
   *
   * Worth being precise about, because the card shows one flag and there are
   * two teams behind it: the row's seeded team, and the team the account is
   * actually on. A claim copies the first onto the second (D60); this writes
   * only the second. They diverge from here, and the survey shows the account's
   * because that is the one the guest sees, their photos carry, and the board
   * counts.
   *
   * A re-assign of the same row would copy the roster's team back over it. That
   * is not a bug to fix here — it is the roster reasserting itself, which is
   * what `assign` is for.
   */
  const moveTeam = async (row: AdminRosterRow, team: Team) => {
    if (!row.claimedBy) return;
    const ok = await run(row.guestId, async () => {
      const res = await api<SetTeamResponse>(`/admin/guests/${row.claimedBy}/team`, {
        method: 'POST', json: { team },
      });
      setTeamNote(
        res.photosReflagged > 0
          ? `${row.claimedByName || row.fullName} is on ${team}. ${res.photosReflagged} photo${res.photosReflagged === 1 ? '' : 's'} re-flagged.`
          : `${row.claimedByName || row.fullName} is on ${team}.`,
      );
    });
    if (ok) setPendingTeam(null);
  };

  /**
   * Browsing shows everything; assigning still needs a query.
   *
   * The cap that used to sit here (`.slice(0, 12)`) was the other half of why
   * this could not answer "who has signed in" — even with a query, it stopped
   * at twelve. A hundred and fifty-nine rows of two lines each is a scroll, not
   * a performance problem, and the filter below is what makes it navigable.
   */
  /**
   * Accounts holding no row that the queue is **not** already showing.
   *
   * Filing a help request is only reachable from a guest who could not find
   * themselves, so by construction they hold no row — which means every queue
   * entry also satisfies `unlinked`'s filter, and the screen listed each of
   * them twice: once above with their note and the buttons that act on it, and
   * again below as a bare name and address with neither.
   *
   * The queue wins, because it is the entry that can be acted on. This panel is
   * for the accounts nothing else on the screen can show.
   */
  const adrift = useMemo(() => {
    const queued = new Set((requests ?? []).map((r) => r.userId));
    return (roster?.unlinked ?? []).filter((a) => !queued.has(a.userId));
  }, [roster, requests]);

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (assigning && !needle) return [];
    return (roster?.rows ?? []).filter((r) => {
      // **Not while assigning**, and this is a correctness bug rather than a
      // tidiness one. The filter's own buttons are hidden in that mode, so a
      // filter left on "Not yet" would keep dropping every claimed row from a
      // picker whose entire purpose includes taking a name off whoever holds
      // it — "a name somebody else took" is the first case in this file's
      // docstring. The organiser types the disputed guest's name, gets
      // "Nobody on the roster matches that", and has no visible control
      // explaining why.
      if (!assigning && filter === 'matched' && !r.claimedBy) return false;
      if (!assigning && filter === 'unmatched' && r.claimedBy) return false;
      if (!needle) return true;
      return r.fullName.toLowerCase().includes(needle)
        || (r.claimedByName ?? '').toLowerCase().includes(needle)
        || (r.claimedByEmail ?? '').toLowerCase().includes(needle);
    });
  }, [roster, q, filter, assigning]);

  return (
    <>
      <h3 style={{ margin: '16px 0 4px' }}>Roster</h3>
      {roster && (
        <p style={{ fontSize: 13, opacity: 0.7, margin: 0 }}>
          {roster.claimed} of {roster.rows.length} matched · {roster.unclaimed} still to arrive
        </p>
      )}
      {error && <p role="alert" style={{ fontSize: 13, color: 'var(--color-accent-700)', marginTop: 12 }}>{error}</p>}

      {/* The queue first. It is the only part of this screen that is a to-do
          list — the roster below is looked up, not worked through. */}
      {requests && requests.length > 0 && (
        <>
          <h4 style={{ margin: '18px 0 6px' }}>Can't find themselves ({requests.length})</h4>
          <ul className="stack" style={{ listStyle: 'none', padding: 0, gap: 8 }}>
            {requests.map((r) => (
              <li key={r.userId} className="card" style={{ padding: 12 }}>
                <strong>{r.displayName || r.email}</strong>
                <div style={{ fontSize: 12, opacity: 0.7 }}>{r.email}</div>
                {r.note && <p style={{ fontSize: 13, margin: '8px 0 0' }}>“{r.note}”</p>}
                <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                  <button
                    type="button" className="btn" disabled={busy === r.userId}
                    onClick={() => { setQ(r.displayName || r.email); setAssigning(r); }}
                  >
                    Match to a name
                  </button>
                  <button type="button" className="btn" disabled={busy === r.userId} onClick={() => { void dismiss(r.userId); }}>
                    Dismiss
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}

      {/* Not shown while assigning: there the list is a picker for one name,
          and a claim-state filter over it would only hide the row you want. */}
      {roster && !assigning && (
        <div className="roster-filters" role="group" aria-label="Filter the roster">
          {([
            ['all', 'Everyone', roster.rows.length],
            ['matched', 'Signed in', roster.claimed],
            ['unmatched', 'Not yet', roster.unclaimed],
          ] as const).map(([key, label, count]) => (
            <button
              key={key} type="button"
              className={`btn btn-tiny${filter === key ? ' btn-primary' : ''}`}
              aria-pressed={filter === key}
              onClick={() => setFilter(key)}
            >
              {label} <span style={{ opacity: 0.7 }}>{count}</span>
            </button>
          ))}
        </div>
      )}

      <div className="field roster-search">
        <label htmlFor="admin-roster-q">
          {assigning ? `Find the name for ${assigning.displayName || assigning.email}` : 'Search the roster'}
        </label>
        <input
          id="admin-roster-q" className="input" autoComplete="off"
          value={q} onChange={(e) => setQ(e.target.value)}
        />
      </div>

      {assigning && !q.trim() && (
        <p style={{ fontSize: 13, opacity: 0.6 }}>Type a name to pick the row to give them.</p>
      )}
      {!assigning && roster && shown.length === 0 && (
        <p style={{ fontSize: 13, opacity: 0.6 }}>
          {/* Two different dead ends, and they are not the same sentence: a
              search that matched nothing is about the query, an empty group is
              about the roster. */}
          {q.trim() ? 'Nobody on the roster matches that.' : 'Nobody in this group yet.'}
        </p>
      )}

      {/* Two to a row while browsing, one while assigning. The survey is a
          density problem — 159 rows read standing up — and the picker is not:
          there the action button carries a sentence, and half a phone's width
          wraps it into a paragraph. */}
      <ul className={assigning ? 'stack roster-list' : 'roster-grid'}>
        {shown.map((r) => (
          <li key={r.guestId} className="card roster-card">
            <div className="roster-row-head">
              {/* **One flag, and it is the account's team when there is an
                  account.** The row's own team is what a guest is dealt on
                  claiming; the account's is what they are actually on. Showing
                  the row's beside a control that edits the account's would make
                  the card lie the moment anybody is moved. */}
              <TeamControl
                row={r}
                disabled={!r.claimedBy || busy === r.guestId || !!assigning}
                onPick={(team) => setPendingTeam({ row: r, team })}
              />
              <strong>{r.fullName}</strong>
              {/* Right-aligned in the header beside the unmatch button, not down
                  in the claim block. Both are facts about the *account* rather
                  than the invitation, and both belong in the corner the name is
                  not using — which is also the only place a chip does not push
                  the facts below it out of line across a row. */}
              {r.claimedByIsAdmin && !assigning && (
                <span className="tag tag-accent-2 roster-tag">Organiser</span>
              )}
              {/* Top right of the card rather than a row beneath it. As a full
                  button it was the widest thing on the card and the only one
                  that moved the facts around — present on a matched row and
                  absent on an unmatched one, so two cards side by side put
                  their dates on different lines. An icon sits in space the
                  name is not using. It keeps a label and a tooltip: this takes
                  a name off somebody, and an unlabelled glyph is not a control
                  to do that from. */}
              {r.claimedBy && !assigning && (
                <button
                  type="button" className="btn-icon roster-unmatch"
                  disabled={busy === r.guestId}
                  aria-label={`Unmatch ${r.fullName}`}
                  title={`Unmatch ${r.fullName}`}
                  onClick={() => { void unclaim(r.guestId); }}
                >
                  <IconUnlink size={15} />
                </button>
              )}
            </div>
            <ClaimLine row={r} at={at} />
            <RosterFacts row={r} />
            <div className="roster-card-actions">
              {assigning && (
                <button
                  type="button" className="btn btn-primary" disabled={busy === r.guestId}
                  onClick={() => {
                    // Only cleared on success: a failure leaves the match and
                    // the typed name exactly where they were, so "try again"
                    // means tapping the same button.
                    void assign(r.guestId, assigning.userId)
                      .then((ok) => { if (ok) { setAssigning(null); setQ(''); } });
                  }}
                >
                  {/* Says what it will do, because on a claimed row it takes the
                      name off whoever holds it. An organiser resolving a dispute
                      should not discover that afterwards. */}
                  {r.claimedBy ? 'Take this name and give it over' : 'Give them this name'}
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>

      {assigning && (
        <button type="button" className="btn btn-block" style={{ marginTop: 12 }} onClick={() => { setAssigning(null); setQ(''); }}>
          Cancel
        </button>
      )}

      {/* **Accounts the survey cannot show**, because every card here is a
          roster row and these are tied to none.

          Not the rare case it looks. Unmatch is a button on every claimed card
          above, self-release is a supported guest action, and both land an
          account here the moment they are used — so the tool an organiser
          reaches for to settle a dispute makes its own subject vanish from the
          screen they are settling it on. Naming them is the whole fix: getting
          one a row back still goes through the queue, which needs the guest to
          ask, and an organiser who cannot see the account has no idea that is
          what is needed.

          Minus anyone the queue is already showing — see `adrift`. A guest who
          asked for help holds no row by definition, so without that filter they
          appeared twice on one screen: once above with their note and the
          buttons that resolve it, and again here with neither. */}
      {adrift.length > 0 && !assigning && (
        <section className="card roster-unlinked">
          <h5 style={{ margin: '0 0 6px' }}>
            {adrift.length === 1 ? 'One account holds no name' : `${adrift.length} accounts hold no name`}
          </h5>
          <ul className="stack" style={{ listStyle: 'none', padding: 0, margin: 0, gap: 4 }}>
            {adrift.map((a) => (
              <li key={a.userId} className="roster-unlinked-row">
                <span>{a.displayName || <em>no name set</em>}</span>
                <span className="roster-unlinked-email">{a.email}</span>
                {a.isAdmin && <span className="tag tag-accent-2 roster-tag">Organiser</span>}
              </li>
            ))}
          </ul>
          <p style={{ fontSize: 12, opacity: 0.7, margin: '8px 0 0' }}>
            They have signed in but hold no roster row — released it, had it unmatched, or never
            claimed one. They can take one from the welcome screen, or ask for help and appear in
            the queue above.
          </p>
        </section>
      )}

      {/* Announced rather than only rendered: an organiser who has just moved
          somebody is looking at the guest, not the screen. */}
      {teamNote && (
        <p role="status" style={{ fontSize: 13, opacity: 0.75, marginTop: 12 }}>{teamNote}</p>
      )}

      {pendingTeam && (
        <ConfirmMove
          row={pendingTeam.row}
          team={pendingTeam.team}
          saving={busy === pendingTeam.row.guestId}
          error={error}
          onCancel={() => setPendingTeam(null)}
          onConfirm={() => { void moveTeam(pendingTeam.row, pendingTeam.team); }}
        />
      )}
    </>
  );
}

/**
 * What an organiser reads off a roster row.
 *
 * **The email is unconditional on a matched row**, which is the change this
 * screen exists for. It used to render as `claimedByName || claimedByEmail`, so
 * the address — the one field that identifies which of somebody's two sign-ins
 * took the name, and the thing to read back to a guest on the phone — was
 * visible only for a guest who had never set a display name. Exactly backwards.
 *
 * `claimedBy` is the `sub`, shown only when the join found neither a name nor an
 * address. That means the guest row is gone while the roster row still points at
 * it, which is a repair an organiser can only make if they can see the id.
 */
/**
 * Confirming a team move (D54).
 *
 * **The copy is the point.** A move re-flags the guest's photographs
 * immediately, but their own claims are frozen until their token turns over
 * (D17) — so an organiser standing next to that guest needs to know what they
 * will and will not see on the phone in their hand, before tapping.
 *
 * Errors render *inside* the sheet rather than on the page behind it: the sheet
 * stays open so the move can be retried, and it is a `position: fixed` scrim,
 * so a message underneath it would be invisible.
 */
function ConfirmMove({ row, team, saving, error, onCancel, onConfirm }: {
  row: AdminRosterRow; team: Team; saving: boolean; error: string | null;
  onCancel: () => void; onConfirm: () => void;
}) {
  const from = row.claimedByTeam ?? row.team;
  const who = row.claimedByName || row.fullName;
  return (
    <div className="sheet-scrim" role="dialog" aria-modal="true" aria-label="Confirm the team change">
      <div className="sheet">
        <button type="button" className="sheet-close" onClick={onCancel} aria-label="Cancel">
          <IconClose size={16} />
        </button>

        <h4 style={{ margin: '0 0 4px' }}>Move {who}?</h4>

        <div className="move-from-to">
          <span>{from ? <><Flag team={from} height={20} /> {from}</> : 'No team'}</span>
          <span aria-hidden="true" className="move-arrow">&rarr;</span>
          <span><Flag team={team} height={20} /> {team}</span>
        </div>

        <p style={{ fontSize: 13, opacity: 0.75, margin: '12px 0 0' }}>
          Their photos move to the new flag straight away. Their own screen catches up
          the next time they open their profile, and within the hour either way.
        </p>
        {/* The code has always known this — `moveTeam` and D60 both say it — and
            the organiser doing it did not. A move writes the account; matching
            the same name again writes the roster's own team back over it, which
            looks like the move silently undoing itself weeks later. */}
        <p style={{ fontSize: 12, opacity: 0.6, margin: '8px 0 0' }}>
          Matching this name to an account again will put them back on the roster's team.
        </p>

        {error && (
          <p
            role="alert"
            style={{ fontSize: 13, color: 'var(--color-accent-700)', margin: '14px 0 0', fontWeight: 600 }}
          >
            {error}
          </p>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
          <button type="button" className="btn btn-secondary" style={{ flex: 1 }} onClick={onCancel} disabled={saving}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" style={{ flex: 1 }} onClick={onConfirm} disabled={saving}>
            {saving ? 'Moving…' : `Move to ${team}`}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * The flag, and on a claimed row the control that moves that account's team.
 *
 * A native `<select>` on purpose, inherited from the screen this absorbed: it
 * is the one control that gives a thumb a full-height wheel on iOS and a real
 * listbox on desktop without this file growing a popover, keyboard handling and
 * focus trapping of its own — and this screen is used one-handed, standing up.
 * The flag sits behind it because option text cannot carry an image.
 *
 * **Disabled rather than absent on an unclaimed row.** There is no account to
 * move, but the flag still has to render or the names in a column step in and
 * out as the grid fills with claimed and unclaimed rows alike.
 */
/**
 * The class the flag/select wrapper carries, exported so the stylesheet check in
 * `TeamControl.css.test.ts` asserts against the same string this renders rather
 * than scraping it back out of the JSX. The rule on it is load-bearing — see
 * that file — and the pairing is now guaranteed by construction on this side.
 */
export const TEAM_CONTROL_CLASS = 'roster-team';

function TeamControl({ row, disabled, onPick }: {
  row: AdminRosterRow;
  disabled: boolean;
  onPick: (team: Team) => void;
}) {
  const team = row.claimedByTeam ?? row.team;
  return (
    <label className={TEAM_CONTROL_CLASS}>
      {team
        ? <Flag team={team} height={14} className="guest-flag" />
        : <span className="guest-flag guest-flag-none" aria-hidden="true" />}
      <span className="sr-only">{`Team for ${row.fullName}`}</span>
      <select
        className="guest-team-select"
        disabled={disabled}
        // Says why, because a select that does nothing reads as broken. The
        // common reason is the only one worth naming: this control moves an
        // *account* onto a team, and an unclaimed row has no account behind it.
        title={!row.claimedBy ? `${row.fullName} has not signed in yet, so there is no account to move` : undefined}
        // `''` rather than `undefined`, which React reads as "uncontrolled" and
        // would silently show the first country as though they were on it.
        value={team ?? ''}
        onChange={(e) => {
          const next = e.target.value as Team;
          // Bound to the real team and never to the pending one, so the card
          // keeps showing what is true until the move is confirmed. Cancelling
          // therefore needs no undo.
          if (next && next !== team) onPick(next);
        }}
      >
        {/* Never selectable: an organiser moves somebody onto a team, never
            off one. */}
        {!team && <option value="" disabled>No team</option>}
        {TEAMS.map((t) => <option key={t} value={t}>{t}</option>)}
      </select>
    </label>
  );
}

function ClaimLine({ row, at }: { row: AdminRosterRow; at: number }) {
  if (!row.claimedBy) {
    return <div className="roster-claim roster-claim-open">Not signed in yet</div>;
  }

  const when = row.claimedAt;
  const seen = row.claimedByLastSeen;
  return (
    <div className="roster-claim">
      <div className="roster-claim-name">{row.claimedByName || <em>no name set</em>}</div>
      {row.claimedByEmail
        ? <div className="roster-claim-email">{row.claimedByEmail}</div>
        : <div className="roster-claim-email"><code>{row.claimedBy}</code></div>}
      {/* Claimed-at and last-seen are two different questions and the second is
          the one asked on the night. They share a line because a card two to a
          row has no space for both stacked, and because "claimed Tuesday, here
          20 min ago" reads as one fact about a person. */}
      <div className="roster-claim-when">
        {when != null && (
          // Labelled like its neighbour. Unlabelled, the two dates ran into one
          // string ("Friday 18 Septseen Sunday 18 Oct") and only one of them
          // said what it was. "Claimed" to match its own tooltip and the
          // decision log (D59), not a third word for the same event.
          <span className="roster-claimed" title={`Claimed ${fmtDay(when)} ${fmtTime(when)} Khao Lak time`}>
            Claimed {relativeAgo(when, at)}
          </span>
        )}
        {seen != null && (
          // **Never labelled a login.** The trigger that records it fires on
          // silent token refreshes too, so it is the last time the app was in
          // use — which is the more useful of the two mid-event, and a lie if
          // called the other thing.
          <span className="roster-seen" title={`Last used the app ${fmtDay(seen)} ${fmtTime(seen)} Khao Lak time`}>
            Seen {relativeAgo(seen, at)}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * The same four facts the guest sees on their own card, beside the name here.
 *
 * **Every row shows all four, and a missing one is a dash.** This rendered
 * nothing at all when the columns were empty — the argument being that four
 * placeholders on every card is the opposite of density, in a view whose whole
 * purpose is scanning 159 of them. Two things were wrong with it.
 *
 * The first is that it made the cards different heights depending on how far
 * through the spreadsheet somebody had got, so a grid of them stepped up and
 * down and nothing lined up across a row. The second is what an organiser is
 * actually looking for: this screen is used to find the gaps. A card that says
 * `Arrive —` is *the answer* to "whose flights am I still missing", and one
 * that renders nothing hides it.
 *
 * `household` is deliberately **not** shown, though the row still carries it.
 * It is the only field here about somebody other than this guest, and it is not
 * what an organiser is on this screen to find — the gaps worth hunting are the
 * four above. It stays on the wire because it is organiser-only data that costs
 * nothing to keep and may earn a screen of its own; it simply does not earn
 * space on a card two to a row.
 *
 * Values are printed as written — see `TripCard`, which makes the same call for
 * the same reason.
 */
const MISSING = '\u2014';

/** `4 Nov · TG 225`, either half alone, or nothing. A flight column holding a
 *  note rather than a number is shown as written. */
const withFlight = (day?: string, flight?: string) =>
  [day, flight && (normaliseFlight(flight) ?? flight)].filter(Boolean).join(' · ') || undefined;

function RosterFacts({ row }: { row: AdminRosterRow }) {
  return (
    <dl className="roster-facts">
      {([
        // The flight number beside the date (D71), so "who lands on TG 225"
        // is answerable from this screen. Numbers only — live status is
        // looked up for the guest who holds the row, never for 160 cards.
        ['Arrive', withFlight(row.arrival, row.arrivalFlight)],
        ['Depart', withFlight(row.departure, row.departureFlight)],
        ['Room', row.room],
        ['Table', row.table],
        // Only on a claimed row: an unclaimed one has nobody to have uploaded
        // anything, and a dash there would read as "uploaded none" rather than
        // "nobody holds this". Zero *is* rendered, because "signed in and has
        // not added a photo yet" is exactly what an organiser is looking for.
        ...(row.claimedByUploads === undefined
          ? []
          : [['Photos', String(row.claimedByUploads)] as const]),
      ] as const).map(([k, raw]) => {
        // Collapsed to `undefined` once, so the dash and the dimmed class can
        // never disagree about what counts as missing. They were `??` and a
        // truthy test respectively, which agree on `undefined` and part company
        // on `''` — an empty string would have rendered a blank line wearing
        // the "missing" style instead of a dash. Unreachable today, because
        // `seed-roster.mjs` normalises empty CSV cells with `|| undefined` and
        // no route can write these fields, but the two conditions sitting one
        // line apart and meaning different things is the trap, not the value.
        const v = raw || undefined;
        return (
          <div key={k}>
            <dt>{k}</dt>
            <dd className={v ? undefined : 'roster-fact-missing'}>
              {v ?? <span aria-label="not set yet">{MISSING}</span>}
            </dd>
          </div>
        );
      })}
    </dl>
  );
}
